import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { brandArticles } from "@/mocks/brand";
import { newsList } from "@/mocks/news";
import { resourceList } from "@/mocks/resources";
import usePageTitle from "@/hooks/usePageTitle";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initialQuery = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(initialQuery);
  const query = initialQuery.trim().toLowerCase();
  usePageTitle("全站搜索");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    setParams(value ? { q: value } : {});
  };

  const result = useMemo(() => {
    if (!query) {
      return { brand: [], news: [], resources: [] };
    }
    return {
      brand: brandArticles.filter(
        (item) =>
          item.title.toLowerCase().includes(query) ||
          item.summary.toLowerCase().includes(query),
      ),
      news: newsList.filter(
        (item) =>
          item.title.toLowerCase().includes(query) ||
          item.summary.toLowerCase().includes(query),
      ),
      resources: resourceList.filter(
        (item) =>
          item.title.toLowerCase().includes(query) ||
          item.summary.toLowerCase().includes(query) ||
          item.tags.some((tag) => tag.toLowerCase().includes(query)),
      ),
    };
  }, [query]);

  const total = result.brand.length + result.news.length + result.resources.length;

  return (
    <div className="w-full">
      <section className="border-b border-background-200 bg-background-100 px-4 md:px-8 py-10 md:py-14">
        <div className="mx-auto max-w-4xl flex flex-col gap-5">
          <Breadcrumb items={[{ label: "全站搜索" }]} />
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-950">
            全站搜索
          </h1>
          <p className="text-sm md:text-base text-foreground-600">
            覆盖品牌介绍、资讯标题与资源内容，输入关键词即可跨板块查找。
          </p>
          <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-3 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="搜索品牌介绍、资讯或资源"
                className="flex-1 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 px-7 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>
      </section>

      <section className="px-4 md:px-8 py-10 md:py-14">
        <div className="mx-auto max-w-4xl flex flex-col gap-10">
          {!query && (
            <EmptyState
              title="输入关键词开始搜索"
              description="可以尝试搜索「品牌」「白皮书」「行业动态」等关键词，快速定位你感兴趣的内容。"
            />
          )}

          {query && total === 0 && (
            <EmptyState
              title={`没有找到与「${initialQuery}」相关的内容`}
              description="试着更换关键词，或直接前往品牌介绍、资讯、资源查询页面浏览全部内容。"
              actionLabel="浏览资源查询"
              actionTo="/resources"
            />
          )}

          {query && total > 0 && (
            <p className="text-sm text-foreground-500">
              共找到 <strong className="font-semibold text-foreground-900">{total}</strong> 条与「
              {initialQuery}」相关的结果
            </p>
          )}

          {result.brand.length > 0 && (
            <div className="flex flex-col gap-4">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                <i className="ri-book-2-line text-primary-500" />
                品牌介绍
              </h2>
              <div className="flex flex-col gap-3">
                {result.brand.map((item) => (
                  <Link
                    key={item.id}
                    to={`/brand/${item.id}`}
                    className="group flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                  >
                    <span className="text-xs font-medium text-accent-600">{item.category}</span>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2">{item.summary}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {result.news.length > 0 && (
            <div className="flex flex-col gap-4">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                <i className="ri-newspaper-line text-primary-500" />
                相关资讯
              </h2>
              <div className="flex flex-col gap-3">
                {result.news.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.id}`}
                    className="group flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                  >
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="font-medium text-accent-600">{item.type}</span>
                      <span>{item.date}</span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2">{item.summary}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {result.resources.length > 0 && (
            <div className="flex flex-col gap-4">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                <i className="ri-folder-download-line text-primary-500" />
                资源查询
              </h2>
              <div className="flex flex-col gap-3">
                {result.resources.map((item) => (
                  <Link
                    key={item.id}
                    to={`/resources/${item.id}`}
                    className="group flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                  >
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="font-medium text-accent-600">{item.type}</span>
                      <span>{item.field}</span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2">{item.summary}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}